import Cookie from 'js-cookie';
import { request, apiBase } from '@common/request';

/**
 * Web push — the International app's flow (lib/firebase/messaging.ts +
 * NotificationProvider.enableNotifications), ported to vanilla JS. NOT the
 * .in engine's pushNotification.js: that talks to a different Firebase
 * project through `store-token/` and `gettokenstatus/` routes on its own
 * origin, and its message shape (`data.options` JSON) is India's backend.
 *
 * What this does, in the same order the Next.js provider does it:
 *
 *   1. bail unless the browser supports push and goGlobal.FIREBASE is set
 *   2. permission: if already granted, reconnect silently; if "default",
 *      ask (the Next.js Providers mount with autoRequestPermission=true);
 *      "denied" is final
 *   3. load the Firebase SDK LAZILY — its own chunk, fetched here and nowhere
 *      else, so ~180 KB never sits on the page's critical path
 *   4. register /firebase-messaging-sw.js with the config, API URL and geo
 *      in its URL (the worker cannot read the page's config any other way)
 *   5. getToken(vapidKey) → post SET_FCM_TOKEN to the worker → POST
 *      /notifications/register-device (guests register too, without userId)
 *   6. foreground messages → a native Notification whose click tracks
 *      /notifications/track-click and opens the clickAction
 *
 * Not ported: the notification inbox, its 30 s polling and the in-app toast.
 * Those are React UI in the Next.js app (NotificationProvider + Toast +
 * NotificationsPanel); the bell here keeps showing the buzz banners it
 * already shows. The push registration and delivery are what this file is.
 */

const SW_PATH = '/firebase-messaging-sw.js';
const TOKEN_KEY = 'fcm_token';

let firebaseApp = null;
let messagingInstance = null;
let inFlight = null;

function config() {
  return (window.goGlobal && window.goGlobal.FIREBASE) || null;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** The token of the registered device, '' when none. What /test-notifications/ shows. */
export function getStoredToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Unregister this device — the Next.js provider's disableNotifications:
 * POST /notifications/unregister-device with the token, then forget it.
 * The browser permission stays granted (only the user can revoke that);
 * "disabled" means the backend no longer targets this device.
 */
export async function disablePush() {
  const token = getStoredToken();
  if (token) {
    await request
      .post(`${apiBase()}/notifications/unregister-device`, { fcmToken: token })
      .catch(() => {});
  }
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage may be unavailable */
  }
}

/** International's `user` cookie holds the signed-in user; guests have none. */
function userId() {
  try {
    const raw = Cookie.get('user');
    if (!raw) return undefined;
    const user = JSON.parse(raw);
    const id = user.id || user.userId;
    return id ? String(id) : undefined;
  } catch {
    return undefined;
  }
}

function geoCode() {
  return String((window.goGlobal && window.goGlobal.GEO_CODE) || '').toUpperCase();
}

async function loadFirebase() {
  const cfg = config();
  if (!cfg) return null;
  if (!firebaseApp) {
    const appMod = await import(/* webpackChunkName: "firebase" */ 'firebase/app');
    const apps = appMod.getApps();
    firebaseApp = apps.length ? apps[0] : appMod.initializeApp(cfg);
  }
  if (!messagingInstance) {
    const msgMod = await import(/* webpackChunkName: "firebase" */ 'firebase/messaging');
    if (!(await msgMod.isSupported())) return null;
    messagingInstance = msgMod.getMessaging(firebaseApp);
  }
  return messagingInstance;
}

/**
 * The worker's URL carries everything it needs. Base64 JSON rather than a
 * dozen query params, and the same URL every load: a changed config is a
 * different worker URL, which is how the browser knows to update it.
 */
function serviceWorkerUrl() {
  const cfg = config();
  const params = new URLSearchParams({
    cfg: btoa(JSON.stringify(cfg)),
    apiUrl: apiBase(),
    geo: geoCode(),
  });
  return `${SW_PATH}?${params.toString()}`;
}

async function registerServiceWorker() {
  try {
    return await navigator.serviceWorker.register(serviceWorkerUrl(), { scope: '/' });
  } catch (e) {
    return null;
  }
}

function browserName() {
  const ua = navigator.userAgent;
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  return 'unknown';
}
function browserVersion() {
  const m = navigator.userAgent.match(/(Chrome|Firefox|Safari|Edg|OPR)\/(\d+)/);
  return m ? m[2] : 'unknown';
}
function osName() {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Android')) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (ua.includes('Linux')) return 'Linux';
  return 'unknown';
}

/** POST /notifications/register-device — request.post adds the identity headers. */
async function registerDevice(fcmToken) {
  const body = {
    fcmToken,
    userId: userId(),
    platform: 'web',
    browserName: browserName(),
    browserVersion: browserVersion(),
    osName: osName(),
  };
  const res = await request.post(`${apiBase()}/notifications/register-device`, body, {
    throwErrors: true,
  });
  return res !== null && res !== undefined;
}

function trackClick(notificationId, fcmToken) {
  if (!notificationId) return;
  request
    .post(`${apiBase()}/notifications/track-click`, {
      notificationId,
      fcmToken: fcmToken || undefined,
      userId: userId(),
    })
    .catch(() => {});
}

/**
 * A message while the page is open. The worker only handles background
 * delivery, so the page shows these itself — a native Notification, as the
 * Next.js provider does beside its toast.
 */
async function listenForeground(messaging, fcmToken) {
  const { onMessage } = await import(/* webpackChunkName: "firebase" */ 'firebase/messaging');
  onMessage(messaging, (payload) => {
    const d = payload.data || {};
    const n = payload.notification || {};
    const title = d.title || n.title || 'New Notification';
    const clickAction = d.clickAction || (payload.fcmOptions && payload.fcmOptions.link) || '';
    // Anyone on the page can watch deliveries — /test-notifications/ lists
    // them. Fired before the permission check so the page sees the message
    // even when the native Notification below cannot show.
    try {
      window.dispatchEvent(
        new CustomEvent('go:push-message', {
          detail: { title, body: d.body || n.body || '', receivedAt: Date.now() },
        }),
      );
    } catch {
      /* CustomEvent unavailable — the native notification below still shows */
    }
    if (Notification.permission !== 'granted') return;
    try {
      const note = new Notification(title, {
        body: d.body || n.body || '',
        icon: d.icon || d.imageUrl || n.image || '/web-app-manifest-192x192.png',
        image: d.imageUrl || n.image,
        tag: d.notificationId || String(Date.now()),
      });
      note.onclick = () => {
        trackClick(d.notificationId, fcmToken);
        if (clickAction) window.open(clickAction, '_blank');
        note.close();
      };
    } catch {
      /* some browsers refuse page-created notifications; the SW path still works */
    }
  });
}

/**
 * Permission + token + registration. Returns true when the device is
 * registered. Idempotent and de-duplicated: the bell and the idle init can
 * both call it, and only one flow runs at a time.
 */
export function enablePush() {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      if (!isPushSupported() || !config()) return false;

      if (Notification.permission !== 'granted') {
        const result = await Notification.requestPermission();
        if (result !== 'granted') return false;
      }

      const messaging = await loadFirebase();
      if (!messaging) return false;

      const registration = await registerServiceWorker();
      if (!registration) return false;

      const { getToken } = await import(/* webpackChunkName: "firebase" */ 'firebase/messaging');
      const token = await getToken(messaging, {
        vapidKey: config().vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return false;

      // Hand the token to the worker for background click tracking.
      const target = registration.active || navigator.serviceWorker.controller;
      if (target) target.postMessage({ type: 'SET_FCM_TOKEN', fcmToken: token });

      const registered = await registerDevice(token);
      if (registered) {
        try {
          window.localStorage.setItem(TOKEN_KEY, token);
        } catch {
          /* storage may be unavailable */
        }
        await listenForeground(messaging, token);
      }
      return registered;
    } catch (e) {
      return false;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Page-load behaviour, matching the Next.js provider mounted with
 * autoRequestPermission=true: reconnect when already granted, ask when
 * "default", never nag after "denied". Called from @core at idle.
 */
export function firebasePushInit() {
  if (!isPushSupported() || !config()) return;
  const permission = Notification.permission;
  if (permission === 'denied') return;
  enablePush();
}
